import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Users, UserCircle2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useWorkspace } from '@/hooks/useWorkspace'
import { updateWidgetVisibility } from '@/services/api/dashboard.service'
import { getRoles } from '@/services/api/permission.service'
import { getWorkspaceUsers } from '@/services/api/task.service'
import { cn } from '@/utils/cn'
import { logger } from '@/utils/logger'

interface DashboardVisibilityDialogProps {
  open: boolean
  widgetId: string
  dashboardId: string
  /** Current restriction (empty = widget inherits dashboard visibility). */
  initialRoleIds?: string[]
  initialUserIds?: string[]
  onOpenChange: (open: boolean) => void
}

export function DashboardVisibilityDialog({
  open,
  widgetId,
  dashboardId,
  initialRoleIds,
  initialUserIds,
  onOpenChange,
}: DashboardVisibilityDialogProps) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setSelectedRoleIds(initialRoleIds ?? [])
      setSelectedUserIds(initialUserIds ?? [])
    }
  }, [open, initialRoleIds, initialUserIds])

  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
    enabled: open,
    staleTime: 60 * 1000,
  })

  const usersQuery = useQuery({
    queryKey: ['workspace-users'],
    queryFn: getWorkspaceUsers,
    enabled: open,
    staleTime: 60 * 1000,
  })
  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    )
  }

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    )
  }

  const handleSave = async () => {
    if (!dashboardId) return
    setIsSaving(true)
    try {
      await updateWidgetVisibility(dashboardId, widgetId, {
        roleIds: selectedRoleIds,
        userIds: selectedUserIds,
      })
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
      onOpenChange(false)
    } catch (err) {
      logger.error('Failed to update widget visibility', err, 'expected')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-sm">
              <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
            </div>
            <DialogTitle className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Widget Visibility</DialogTitle>
          </div>
          <DialogDescription>
            Choose who can see this widget. When nothing is selected, the widget inherits
            the dashboard's default visibility (everyone with access to the dashboard).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {open && (
            <>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-neutral-400">
                  <Users className="h-3.5 w-3.5" /> Grant access to roles
                </Label>
                {rolesQuery.isLoading ? (
                  <Skeleton className="h-20 w-full rounded-lg" />
                ) : (
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-neutral-200 p-2">
                    {(rolesQuery.data ?? []).length === 0 ? (
                      <p className="px-1 py-1 text-xs text-neutral-400">No roles available</p>
                    ) : (
                      (rolesQuery.data ?? []).map((role) => (
                        <label
                          key={role.id}
                          className={cn(
                            'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-neutral-50',
                            selectedRoleIds.includes(role.id) && 'bg-primary-50'
                          )}
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded accent-primary-500"
                            checked={selectedRoleIds.includes(role.id)}
                            onChange={() => toggleRole(role.id)}
                          />
                          <span className="text-neutral-700">{role.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-neutral-400">
                  <UserCircle2 className="h-3.5 w-3.5" /> Grant access to specific users
                </Label>
                {usersQuery.isLoading ? (
                  <Skeleton className="h-20 w-full rounded-lg" />
                ) : (
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-neutral-200 p-2">
                    {(usersQuery.data ?? []).length === 0 ? (
                      <p className="px-1 py-1 text-xs text-neutral-400">No users available</p>
                    ) : (
                      (usersQuery.data ?? []).map((user) => (
                        <label
                          key={user.userId}
                          className={cn(
                            'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-neutral-50',
                            selectedUserIds.includes(user.userId) && 'bg-primary-50'
                          )}
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded accent-primary-500"
                            checked={selectedUserIds.includes(user.userId)}
                            onChange={() => toggleUser(user.userId)}
                          />
                          <span className="text-neutral-700">{user.name}</span>
                          <span className="ml-auto truncate text-xs text-neutral-400">{user.email}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving} className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-sm hover:from-emerald-700 hover:to-teal-700">
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
